import React, { useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, FileDown, Send, Ban, FileSearch, ShieldAlert, IdCard, TriangleAlert, Upload, Paperclip, Trash2, FileText, Image as ImageIcon } from "lucide-react";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const RACE = ["W - White","B - Black","H - Hispanic","A - Asian","I - Native American","O - Other"];
const SEX = ["M - Male","F - Female","X - Non-binary/Other"];

// ---------------- Schemas (per screenshots) ----------------
export const FORM_SCHEMAS = {
  banning: {
    title: "Banning Notice / Trespass Warning", icon: Ban, accent: "#DC2626", submitLabel: "Issue Banning Notice",
    requireState: true, sections: [
      { label: "Property / Location", fields: [
        { k: "property_name", l: "Property / Business Name", ph: "e.g. Walmart #1234, ABC Apartments", span: 2 },
        { k: "property_address", l: "Property Address", required: true, ph: "Full address", span: 2 },
        { k: "rep_name", l: "Property Rep Name", ph: "Manager / Owner name" },
        { k: "rep_title", l: "Rep Title", ph: "Store Manager, etc." } ] },
      { label: "Subject Information", fields: [
        { k: "subject_name", l: "Full Name", required: true, ph: "Last, First MI", span: 2 },
        { k: "dob", l: "DOB", type: "date" }, { k: "phone", l: "Phone", type: "tel", ph: "(555) 000-0000" },
        { k: "race", l: "Race", type: "select", options: RACE }, { k: "sex", l: "Sex", type: "select", options: SEX },
        { k: "height", l: "Height", ph: "5'10\"" }, { k: "weight", l: "Weight", ph: "175 lbs" },
        { k: "hair", l: "Hair Color", ph: "BRN" }, { k: "eyes", l: "Eye Color", ph: "BRN" },
        { k: "address", l: "Address", ph: "Subject's home address", span: 2 },
        { k: "id_number", l: "ID / DL Number", ph: "LICENSE / ID #" },
        { k: "id_state", l: "ID Issuing State", type: "select", options: US_STATES } ] },
    ], pdf: true, attachments: true,
  },
  warrant: {
    title: "Search Warrant Request", icon: FileSearch, accent: "#F59E0B", submitLabel: "Submit Warrant Application",
    sections: [
      { label: "Warrant", fields: [
        { k: "warrant_type", l: "Warrant Type", type: "select", options: ["Search & Seizure","Arrest Warrant","Bench Warrant","Anticipatory","Electronic / Digital"] },
        { k: "urgency", l: "Urgency", type: "select", options: ["Standard","Priority","Emergency / Exigent"] },
        { k: "target_address", l: "Target Address / Premises", required: true, ph: "123 Main St, Baltimore MD…", span: 2 },
        { k: "target_person", l: "Target Person (if applicable)", ph: "Name, DOB, description…", span: 2 },
        { k: "items", l: "Items / Evidence to be Seized", type: "textarea", required: true, ph: "Describe items, contraband, or evidence sought…", span: 2 },
        { k: "linked_cc", l: "Linked Incident #", ph: "2026-XXXXX" },
        { k: "pc_basis", l: "Probable Cause Basis", type: "multiselect", span: 2,
          options: ["Officer observation","Confidential informant","Controlled buy","Surveillance","Victim/witness statement","Physical evidence","Digital/records"] },
        { k: "pc_statement", l: "Probable Cause Statement", type: "textarea", required: true, ph: "Describe observed facts, reliable information, and basis for probable cause…", span: 2 },
        { k: "requesting_officer", l: "Requesting Officer", ph: "Badge #" },
        { k: "judge_ada", l: "Judge / ADA Name", ph: "Optional" } ] },
    ],
  },
  use_of_force: {
    title: "Use of Force Report", icon: ShieldAlert, accent: "#EF4444", critical: true, submitLabel: "Submit UOF Report",
    banner: "Dispatch will be automatically notified. Supervisor must be informed per department policy.",
    sections: [
      { label: "Subject Information", fields: [
        { k: "subject_name", l: "Subject Name", ph: "Last, First" }, { k: "dob", l: "DOB", type: "date" },
        { k: "location", l: "Location of Incident", span: 2 } ] },
      { label: "Force Details", fields: [
        { k: "force_type", l: "Type of Force Used", type: "select", required: true,
          options: ["Verbal commands","Soft empty-hand control","Hard empty-hand control","OC / Chemical spray","Taser / CEW","Baton / Impact","K-9 deployment","Firearm displayed","Firearm discharged","Vehicle intervention"] },
        { k: "subject_action", l: "Subject's Action", type: "select",
          options: ["Passive resistance","Active resistance","Assaultive","Aggravated / deadly threat","Fleeing"] },
        { k: "weapons", l: "Weapons Involved", ph: "Firearm, knife, none…", span: 2 } ] },
      { label: "Injuries & Medical", fields: [
        { k: "subject_injury", l: "Subject Injury", type: "select", options: ["No","Yes"] },
        { k: "officer_injury", l: "Officer Injury", type: "select", options: ["No","Yes"] },
        { k: "medical_rendered", l: "Medical Rendered", type: "select", options: ["No","Yes"] } ] },
      { label: "Supervisor Notification", fields: [
        { k: "supervisor_notified", l: "Supervisor Notified", type: "select", options: ["No","Yes"] },
        { k: "supervisor_name", l: "Supervisor Name", ph: "Name or badge #" },
        { k: "narrative", l: "Narrative", type: "textarea", required: true, span: 2,
          ph: "Describe the incident, circumstances leading to force, actions taken, and outcome in detail…" } ] },
    ],
  },
  fi_card: {
    title: "Field Interview / FI Card", icon: IdCard, accent: "#F59E0B", submitLabel: "Submit FI Card",
    sections: [
      { label: "Subject Information", fields: [
        { k: "subject_name", l: "Full Name", required: true, ph: "Last, First MI", span: 2 },
        { k: "dob", l: "DOB", type: "date" }, { k: "phone", l: "Phone", type: "tel", ph: "(555) 000-0000" },
        { k: "race", l: "Race", type: "select", options: RACE }, { k: "sex", l: "Sex", type: "select", options: SEX },
        { k: "height", l: "Height", ph: "5'10\"" }, { k: "weight", l: "Weight", ph: "175 lbs" },
        { k: "hair", l: "Hair Color", ph: "BRN" }, { k: "eyes", l: "Eye Color", ph: "BRN" },
        { k: "address", l: "Address", ph: "Subject's address", span: 2 } ] },
      { label: "Vehicle (if applicable)", fields: [
        { k: "plate", l: "License Plate", ph: "ABC-1234" },
        { k: "vehicle_desc", l: "Description", ph: "2019 BLK Honda Civic" } ] },
      { label: "Circumstances", fields: [
        { k: "location", l: "Location of Contact", ph: "Address / cross streets", span: 2 },
        { k: "reason", l: "Reason for Stop / Contact", type: "textarea", required: true, ph: "Why was this subject contacted?", span: 2 } ] },
    ], pdf: true, attachments: true,
  },
};

function printPdf(schema, data, state, meta) {
  const rows = schema.sections.map((s) => `
    <h3>${s.label}</h3>
    <table>${s.fields.map((f) => {
      const v = Array.isArray(data[f.k]) ? data[f.k].join(", ") : (data[f.k] || "—");
      return `<tr><td class="k">${f.l}</td><td class="v">${String(v).replace(/</g, "&lt;")}</td></tr>`;
    }).join("")}</table>`).join("");
  const w = window.open("", "_blank", "width=820,height=1000");
  if (!w) { toast.error("Pop-up blocked — allow pop-ups to export PDF"); return; }
  w.document.write(`<html><head><title>${schema.title}</title><style>
    body{font-family:Georgia,serif;color:#111;padding:40px;max-width:760px;margin:auto}
    h1{font-size:20px;border-bottom:3px solid #111;padding-bottom:8px}
    h3{font-size:12px;text-transform:uppercase;letter-spacing:1px;color:#444;margin:20px 0 6px}
    table{width:100%;border-collapse:collapse;font-size:13px}
    td{padding:5px 8px;border-bottom:1px solid #ddd;vertical-align:top}
    td.k{width:38%;color:#555;font-weight:bold}
    .meta{font-size:11px;color:#666;margin-bottom:16px}
    .foot{margin-top:30px;font-size:10px;color:#888;border-top:1px solid #ccc;padding-top:8px}
  </style></head><body>
    <h1>${schema.title}</h1>
    <div class="meta">Matcom Office Of Public Safety${state ? " · Issuing State: " + state : ""}${meta?.report_number ? " · " + meta.report_number : ""}<br/>
      Prepared by ${meta?.by || ""} · ${new Date().toLocaleString()}</div>
    ${rows}
    <div class="foot">Official record — subject to audit. Generated by Matcom Integrated CAD.</div>
  </body></html>`);
  w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
}

export default function FieldFormModal({ type, open, onClose, incidentCc, unit, defaultState = "", onSubmitted }) {
  const schema = FORM_SCHEMAS[type];
  const [data, setData] = useState({ linked_cc: incidentCc || "" });
  const [state, setState] = useState(defaultState);
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [selectedCc, setSelectedCc] = useState(incidentCc || "");
  const [manualCc, setManualCc] = useState("");

  React.useEffect(() => {
    if (open) {
      setData({ linked_cc: incidentCc || "" }); setState(defaultState || ""); setFiles([]);
      setSelectedCc(incidentCc || ""); setManualCc("");
      client.get("/incidents", { params: { active: true } }).then((r) => setIncidents(r.data || [])).catch(() => setIncidents([]));
    }
  }, [open, incidentCc, defaultState]);

  if (!open || !schema) return null;
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleMulti = (k, v) => setData((d) => {
    const cur = d[k] || []; return { ...d, [k]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] }; });

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const ok = [];
    for (const f of picked) {
      if (!/^(image\/(jpeg|png|webp)|application\/pdf)$/.test(f.type)) { toast.error(`${f.name}: images or PDF only`); continue; }
      if (f.size > 15_000_000) { toast.error(`${f.name}: max 15MB`); continue; }
      ok.push(f);
    }
    setFiles((prev) => [...prev, ...ok]);
    e.target.value = "";
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const validate = () => {
    if (schema.requireState && !state) { toast.error("Issuing state jurisdiction is required"); return false; }
    for (const s of schema.sections) for (const f of s.fields)
      if (f.required && !(data[f.k] || "").toString().trim()) { toast.error(`${f.l} is required`); return false; }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const cc = (manualCc.trim() || selectedCc || data.linked_cc || "").trim() || null;
      const { data: res } = await client.post("/forms", {
        form_type: type, incident_cc: cc,
        state: state || null, unit: unit || null, data });
      if (files.length && res.id) {
        try {
          const fd = new FormData();
          files.forEach((f) => fd.append("files", f));
          await client.post(`/forms/${res.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (ue) {
          toast.warning(`${schema.title} filed — ${res.report_number}, but ${files.length} attachment(s) failed: ${ue.response?.data?.detail || "retry from records"}.`);
          onSubmitted?.(res); onClose?.(); return;
        }
      }
      toast.success(`${schema.title} filed — ${res.report_number}${files.length ? ` · ${files.length} attachment(s)` : ""}`);
      onSubmitted?.(res); onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  const Icon = schema.icon;
  const inp = "w-full bg-[#141518] border border-[#2C2D35] px-3 py-2 text-sm outline-none focus:border-[#3B82F6] font-mono text-white placeholder:text-[#4B5563]";

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid={`form-modal-${type}`}>
      <div className="w-full max-w-2xl max-h-[92vh] flex flex-col bg-[#0d0e11] border shadow-2xl" style={{ borderColor: schema.accent + "55" }}>
        {/* header */}
        <div className="shrink-0 flex items-center gap-3 px-5 py-4 border-b border-[#2C2D35]">
          <div className="w-9 h-9 flex items-center justify-center" style={{ background: schema.accent + "22", border: `1px solid ${schema.accent}66` }}>
            <Icon size={18} style={{ color: schema.accent }} />
          </div>
          <div className="font-head font-bold text-white tracking-tight leading-none flex items-center gap-2">
            {schema.title.toUpperCase()}
            {schema.critical && <span className="font-mono text-[9px] px-1.5 py-0.5 bg-[#DC2626] text-white">CRITICAL</span>}
          </div>
          <button data-testid={`form-close-${type}`} onClick={onClose} className="ml-auto text-[#9CA3AF] hover:text-white"><X size={20} /></button>
        </div>

        {schema.banner && (
          <div className="shrink-0 mx-5 mt-4 px-3 py-2 border border-[#EF4444]/40 bg-[#EF4444]/10 flex gap-2">
            <TriangleAlert size={14} className="text-[#EF4444] shrink-0 mt-0.5" />
            <p className="font-mono text-[10px] leading-relaxed text-[#EF4444]">{schema.banner}</p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {schema.requireState && (
            <div className="px-3 py-3 border border-[#DC2626]/40 bg-[#DC2626]/8">
              <label className="font-mono text-[10px] text-[#DC2626] uppercase tracking-wider">⚠ Issuing State Jurisdiction *</label>
              <select data-testid={`form-${type}-state`} value={state} onChange={(e) => setState(e.target.value)} className={`mt-1.5 ${inp}`}>
                <option value="">Select state…</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          <div className="px-3 py-3 border border-[#3B82F6]/30 bg-[#3B82F6]/5" data-testid={`form-${type}-cfs`}>
            <label className="font-mono text-[10px] text-[#3B82F6] uppercase tracking-wider flex items-center gap-1.5"><FileText size={11} /> Associate with Call-for-Service</label>
            <select data-testid={`form-${type}-cfs-select`} value={selectedCc} onChange={(e) => { setSelectedCc(e.target.value); setManualCc(""); }} className={`mt-1.5 ${inp}`}>
              <option value="">— No call association —</option>
              {incidents.map((i) => <option key={i.id} value={i.cc_number}>{i.cc_number} · {i.call_type_label} · {i.location?.address || ""}</option>)}
            </select>
            <input data-testid={`form-${type}-cfs-manual`} value={manualCc} onChange={(e) => setManualCc(e.target.value)} placeholder="or enter another CC # (e.g. 2026-12345)" className={`mt-2 ${inp}`} />
            <div className="mt-1.5 font-mono text-[9px] text-[#9CA3AF]">Selected: <span className="text-[#3B82F6]">{(manualCc.trim() || selectedCc) || "none"}</span> — links this form to the call's event log and its RMS record.</div>
          </div>
          {schema.sections.map((s) => (
            <div key={s.label}>
              <div className="font-mono text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-2.5 pb-1 border-b border-[#2C2D35]">{s.label}</div>
              <div className="grid grid-cols-2 gap-3">
                {s.fields.map((f) => (
                  <div key={f.k} className={f.span === 2 ? "col-span-2" : ""}>
                    <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">{f.l}{f.required && " *"}</label>
                    {f.type === "textarea" ? (
                      <textarea data-testid={`form-${type}-${f.k}`} rows={3} value={data[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} placeholder={f.ph} className={`mt-1 ${inp}`} />
                    ) : f.type === "select" ? (
                      <select data-testid={`form-${type}-${f.k}`} value={data[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} className={`mt-1 ${inp}`}>
                        <option value="">Select…</option>
                        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : f.type === "multiselect" ? (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {f.options.map((o) => {
                          const on = (data[f.k] || []).includes(o);
                          return <button key={o} type="button" data-testid={`form-${type}-${f.k}-${o.replace(/[^a-z0-9]/gi, "").toLowerCase()}`} onClick={() => toggleMulti(f.k, o)}
                            className={`px-2.5 py-1 text-[11px] font-head border transition-colors ${on ? "border-[#F59E0B] bg-[#F59E0B]/15 text-[#F59E0B]" : "border-[#2C2D35] bg-[#141518] text-[#9CA3AF] hover:text-white"}`}>{o}</button>;
                        })}
                      </div>
                    ) : (
                      <input data-testid={`form-${type}-${f.k}`} type={f.type === "date" ? "date" : f.type === "tel" ? "tel" : "text"}
                        value={data[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} placeholder={f.ph} className={`mt-1 ${inp}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {schema.attachments && (
          <div>
            <div className="font-mono text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-2.5 pb-1 border-b border-[#2C2D35] flex items-center gap-1.5">
              <Paperclip size={11} /> Photos / Records (optional)
            </div>
            <label data-testid={`form-${type}-file-picker`}
              className="flex items-center justify-center gap-2 w-full px-3 py-3 border border-dashed border-[#3B82F6]/50 bg-[#141518] text-[#3B82F6] text-xs font-head cursor-pointer hover:bg-[#3B82F6]/10 transition-colors">
              <Upload size={14} /> Add photos or PDF records (images / PDF, max 15MB each)
              <input data-testid={`form-${type}-file-input`} type="file" multiple
                accept="image/png,image/jpeg,image/webp,image/heic,image/heif,application/pdf" className="hidden" onChange={onFiles} />
            </label>
            {files.length > 0 && (
              <div className="mt-2 space-y-1.5" data-testid={`form-${type}-file-list`}>
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 px-2.5 py-2 bg-[#141518] border border-[#2C2D35]">
                    {f.type === "application/pdf"
                      ? <FileText size={14} className="text-[#F59E0B] shrink-0" />
                      : <ImageIcon size={14} className="text-[#3B82F6] shrink-0" />}
                    <span className="flex-1 min-w-0 truncate font-mono text-[11px] text-white">{f.name}</span>
                    <span className="font-mono text-[10px] text-[#9CA3AF]">{(f.size / 1024 / 1024).toFixed(1)}MB</span>
                    <button type="button" data-testid={`form-${type}-file-remove-${i}`} onClick={() => removeFile(i)}
                      className="text-[#9CA3AF] hover:text-[#DC2626]"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* footer */}
        <div className="shrink-0 flex items-center gap-3 px-5 py-3 border-t border-[#2C2D35] bg-[#0a0b0d]">
          <button data-testid={`form-cancel-${type}`} onClick={onClose} className="px-4 py-2 text-xs font-head bg-[#1E1F24] border border-[#2C2D35] text-[#9CA3AF] hover:text-white">Cancel</button>
          {schema.pdf && (
            <button data-testid={`form-pdf-${type}`} onClick={() => printPdf(schema, data, state)}
              className="px-4 py-2 text-xs font-head bg-[#141518] border border-[#F59E0B] text-[#F59E0B] flex items-center gap-1.5 hover:bg-[#F59E0B]/10">
              <FileDown size={13} /> PDF
            </button>
          )}
          <button data-testid={`form-submit-${type}`} onClick={submit} disabled={busy}
            className="ml-auto px-5 py-2 text-xs font-head text-white flex items-center gap-1.5 transition-colors disabled:opacity-50"
            style={{ background: schema.accent }}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} {schema.submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
